import { useTranslation } from 'next-i18next';
import { useEffect, useMemo, useState } from 'react';
import { lastValueFrom } from 'rxjs';

import { NS } from '@/constants';
import { useServices } from '@/services/useServices';
import { useAppDispatch, useAppSelector } from '@/redux';
import { isObjectEmpty } from '@/utils/comparisonUtils';
import { formatKebabCaseDate, formatLiteralDate, formatSlotToLocaleString } from '@/utils/dateUtils';
import { TopChronoDataModel } from '@/models';

import { updateTopChronoData } from '@/redux/slices/cart.slice';
import { updateTopChronoDataExpro } from '@/redux/slices/expro.slice';

import { toggleTopChronoModal } from '@/redux/slices/gui.slice';

import TimeSlotSelector from '@/components/checkout/shipMode/DeliverySelector/TimeSlotSelector';
import CustomCalendar from '@/components/common/calendar/CustomCalendar';

import styles from './TopChronoDateSelector.module.scss';

const FORM_ID = 'timeslot';

type TopChronoDateSelectorProps = {
  onSelectedSlot: (slot: {
    phoneNumber: string,
    start: string,
    end: string
  }) => void,
  withPadding: boolean,
  isExproCheckout: boolean
}

export const TopChronoDateSelector = ({ onSelectedSlot, withPadding, isExproCheckout }: TopChronoDateSelectorProps)
  : React.JSX.Element => {
  const { t } = useTranslation(NS.CHECKOUT);
  const dispatch = useAppDispatch();

  const { shippingId } = useAppSelector(({ gui }) => gui);
  const topChronoData = useAppSelector(({ cart }) => cart.topChronoData);
  const topChronoDataExpro = useAppSelector(({ expro }) => expro.topChronoDataExpro);
  const locale = useAppSelector(({ root }) => root.storeConf.codeISO.toLowerCase());

  const { shippingService } = useServices();

  const [bookingSlot, setBookinSlot] = useState(isExproCheckout ? topChronoDataExpro : topChronoData);
  const [selectedDay, setSelectedDay] = useState<string>();
  const [slots, setSlots] = useState([]);
  const [daysName, setDaysName] = useState([]);

  const containerClassName = [
    styles.container,
    withPadding && styles['with-padding']
  ].filter(Boolean).join(' ');

  /**
   * @param {object} slot
   * @return {list} list of short day names depends on local (ex: ['lun.', 'mar.'])
   */
  const getDaysName = (slot) => (
    slot.map((day) => (
      new Date(day?.dayOfShipping).toLocaleDateString(locale, { weekday: 'short' })
    ))
  );

  useEffect(() => {
    const fetchData = async () => {
      const data: any[] = await lastValueFrom(shippingService.getAvailableSlotsTopChrono(shippingId));
      setSlots(data);

      if (data.length > 0) {
        setDaysName(getDaysName(data[0]));
      }

      return data;
    };

    fetchData().then((data) => {
      if (isObjectEmpty(bookingSlot)) {
        const firstDayAvailable = data?.flat().find((day) => day.shippingPossible === true)?.dayOfShipping;
        setSelectedDay(formatKebabCaseDate(firstDayAvailable, locale));
      }
    }).catch(console.error);
  }, []);

  useEffect(() => {
    if (!isObjectEmpty(bookingSlot)) {
      setSelectedDay(bookingSlot.day);
    }
  }, []);

  useEffect(() => {
    if (!isExproCheckout) {
      setBookinSlot(topChronoData);
    }
  }, [topChronoData]);

  useEffect(() => {
    if (isExproCheckout) {
      setBookinSlot(topChronoDataExpro);
    }
  }, [topChronoDataExpro]);

  const selectedSlots = useMemo(() => {
    return slots.flat().find((day) => (
      formatKebabCaseDate(day.dayOfShipping, locale) === selectedDay
    ))?.timeSlots;
  }, [slots, selectedDay]);

  const handleClick = (day) => {
    setSelectedDay(formatKebabCaseDate(day, locale));
  };

  const handleSubmit = (data) => {
    const splittedSlot = data?.slot?.split('|');
    const start = splittedSlot[0];
    const end = splittedSlot[1];

    const bookingSlotData = {
      phoneNumber: data.phoneNumber,
      ...formatSlotToLocaleString(splittedSlot),
      selectedSlot: data.slot
    } as TopChronoDataModel;

    if (isExproCheckout) {
      dispatch(updateTopChronoDataExpro({ data: bookingSlotData }));
    } else {
      dispatch(updateTopChronoData({ data: bookingSlotData }));
    }

    dispatch(toggleTopChronoModal({ opened: false, shippingId: undefined }));

    onSelectedSlot({
      phoneNumber: data.phoneNumber,
      start,
      end
    });
  };

  return (
    <>
      <div className={containerClassName}>
        <span className={styles.subtitle}>{t('shipMode.topChrono.deliveryDate')} </span>
        <span className={styles['selected-day']}>{selectedDay && formatLiteralDate(selectedDay, locale)}</span>
        {daysName && <CustomCalendar
          dayNames={daysName}
          weekList={slots}
          numberOfDisplayedWeeks={2}
          selectedDay={selectedDay}
          onClick={handleClick}
        />}
        {selectedSlots?.length > 0 && (
          <TimeSlotSelector
            t={t}
            formId={FORM_ID}
            timeSlots={selectedSlots}
            onSubmit={handleSubmit}
            defaultValues={{ phoneNumber: bookingSlot.phoneNumber, slot: { value: bookingSlot.selectedSlot } }}
            selectedSlot={bookingSlot.selectedSlot}
          />
        )}
      </div>
    </>
  );
};

export default TopChronoDateSelector;
